/**
 * Modelo de dominio del proyecto (doc. 03). Un proyecto es un documento que se guarda y versiona
 * completo; contiene espacios, y cada espacio tiene su layout y su modelo operativo.
 * Coordenadas de planta en metros: x a la derecha, z hacia abajo; ángulos en radianes.
 */

import type { DefinicionDeProceso, Rol, TipoBahiaActiva, TipoEstacion } from './proceso';
import type { Parametro } from './parametros';
import type { DefinicionDeSemaforo } from './semaforo';

export const VERSION_ESQUEMA = 5;

export interface Punto {
  x: number;
  z: number;
}

/** Tipo de bahía de un elevador (doc. 03 §3.2). `inactivo` = no se usa en la simulación. */
export type TipoBahia = TipoBahiaActiva | 'inactivo';

export interface Elemento {
  id: string;
  catalogoId: string;
  posicion: Punto;
  /** Radianes. El eje local +z apunta a (sin r, cos r): es el frente del elemento. */
  rotacion: number;
  dimensiones: { ancho: number; fondo: number; alto: number };
  etiqueta?: string;
  /** Nombre propio de la pieza (paso 1.4), por ejemplo «Elevador de alineación»; si falta, el del catálogo. */
  nombre?: string;
  propiedades?: { tipoBahia?: TipoBahia };
}

export type TipoVia = 'vehicular-doble' | 'vehicular-un-sentido' | 'peatonal';

export interface Via {
  id: string;
  tipo: TipoVia;
  /** Al menos 2 puntos; el primero marca el inicio del sentido. */
  puntos: Punto[];
  /** Metros. */
  ancho: number;
}

/**
 * Acabados de piso (paso 1.4): los 12 del catálogo del v6. Solo se ven; no cambian la simulación
 * (una «calzada» de piso no es una vía: los autos no la usan).
 */
export type AcabadoPiso =
  | 'epoxico'
  | 'concreto'
  | 'asfalto'
  | 'calzada'
  | 'vereda'
  | 'cesped'
  | 'ceramico'
  | 'recepcion'
  | 'lavado'
  | 'entrega'
  | 'seguridad'
  | 'cebra';

/** Área de piso con acabado: un polígono editable (paso 1.4). */
export interface Piso {
  id: string;
  acabado: AcabadoPiso;
  /** Polígono cerrado en planta: al menos 3 puntos, sin repetir el primero al final. */
  puntos: Punto[];
}

/** Muro: su eje es una línea de puntos (paso 1.4); ocupa piso y se ve con su espesor y alto. */
export interface Muro {
  id: string;
  /** Al menos 2 puntos. */
  puntos: Punto[];
  /** Metros. */
  espesor: number;
  alto: number;
}

export interface Layout {
  elementos: Elemento[];
  vias: Via[];
  /** Áreas de piso con acabado (esquema 4, paso 1.4). */
  pisos: Piso[];
  /** Muros (esquema 4, paso 1.4). */
  muros: Muro[];
  /** Aumenta con cada cambio del layout. */
  versionLayout: number;
}

export interface CitaDeAgenda {
  dia: number;
  hora: string;
  tipoUnidad: string;
  /** Atributos forzados (casos de prueba); los omitidos se muestrean. */
  atributos?: Record<string, boolean>;
}

export interface ParametrosDemanda {
  /** `perfil`: N unidades por día repartidas por hora; `agenda`: lista explícita de citas. */
  modo: 'perfil' | 'agenda';
  unidadesPorDia: Parametro<number>;
  /** Fracción de las citas en cada hora desde el inicio de citas (suma 1). */
  perfilHorario: Parametro<number[]>;
  /** Fracción por tipo de unidad (suma 1). */
  mezcla: Parametro<Record<string, number>>;
  agenda?: CitaDeAgenda[];
}

export interface ParametrosJornada {
  inicioTurno: Parametro<string>;
  finTurno: Parametro<string>;
  inicioCitas: Parametro<string>;
  ultimaCita: Parametro<string>;
  /** Fin del día simulado: foto de pendientes. */
  cierre: Parametro<string>;
}

export interface ParametrosMovimiento {
  /** `distancia`: por la ruta del layout; `fijo`: tiempo constante entre lugares (casos de prueba). */
  modo: 'distancia' | 'fijo';
  velocidadVehiculoKmh: Parametro<number>;
  maniobraS: Parametro<number>;
  trasladoFijoMin: Parametro<number>;
  /**
   * Velocidad de las personas a pie (paso 1.6, avance 2; 4.5 km/h si falta). En modo `fijo`, cada
   * tramo a pie entre dos puntos distintos dura lo mismo que un traslado.
   */
  velocidadPeatonKmh?: Parametro<number>;
}

export interface Politicas {
  /** Elección de estación entre las compatibles y libres. */
  seleccionEstacion: 'mas-cercana' | 'menor-codigo';
  /** Elección de técnico. */
  seleccionPersona: 'menor-carga' | 'menor-codigo';
  /**
   * Lavado después de hora: el lavado puede empezar después del fin del turno, hasta el cierre,
   * para terminar los autos que los técnicos terminaron en horas extra. Si es `false`, el lavado
   * sigue el turno como las demás estaciones.
   */
  lavadoDespuesDeHora: boolean;
  /**
   * Quién va primero cuando varios autos esperan lo mismo: por orden de llegada a la cola
   * (`llegada`, por defecto) o la promesa más cercana primero (`promesa`; los autos aún sin OT
   * abierta siguen por llegada). Paso 1.2.
   */
  ordenColas?: 'llegada' | 'promesa';
  /**
   * Los autos se ceden el paso en las vías (paso 1.6, avance 3; ADR 0012): un traslado solo empieza
   * si puede reservar toda su ruta; si no, el auto espera en su origen. Solo con movimiento por
   * distancia. Sin el campo, los traslados no interactúan (como hasta la 1.5).
   */
  cederElPaso?: boolean;
}

/** Refrigerio de los técnicos (paso 1.2). */
export interface RefrigerioTecnicos {
  /** Hora de inicio (hh:mm). */
  inicio: Parametro<string>;
  /** Minutos. */
  duracionMin: Parametro<number>;
  /**
   * `todos`: paran todos a la vez. `escalonado`: en dos grupos (T01, T03… a la hora de inicio y
   * T02, T04… cuando vuelve el primero).
   */
  modo: Parametro<'todos' | 'escalonado'>;
}

export type Dotacion = Record<'asesor' | 'tecnico', Parametro<number>> &
  Partial<Record<Exclude<Rol, 'asesor' | 'tecnico'>, Parametro<number>>>;

export interface ModeloOperativo {
  proceso: DefinicionDeProceso;
  /**
   * Personas por rol. Asesores y técnicos siempre; jefe de taller y controlador desde la 1.6 (un
   * proyecto anterior no los tiene: cero).
   */
  dotacion: Dotacion;
  demanda: ParametrosDemanda;
  jornada: ParametrosJornada;
  /** Horario de atención para promesas (horas hábiles, decisión P2). */
  atencion: { inicio: Parametro<string>; fin: Parametro<string> };
  /** Redondeo de la hora prometida hacia arriba, en minutos (0 = sin redondeo). */
  redondeoPromesaMin: Parametro<number>;
  movimiento: ParametrosMovimiento;
  politicas: Politicas;
  /**
   * Refrigerio de los técnicos: no empiezan trabajo nuevo durante él; si a esa hora están con un
   * auto, salen al terminar con ese auto y descansan la duración completa. Sin él, no hay pausa.
   */
  refrigerio?: RefrigerioTecnicos;
  /**
   * Cada técnico toma el elevador que esté libre (`libre`, como simula el motor) o trabaja en uno
   * fijo (`fijo`), con las reglas F1–F4 del ADR 0021. El motor lo simula desde el paso 1.6.
   */
  tecnicoPorElevador?: Parametro<'libre' | 'fijo'>;
}

export interface ConfiguracionAleatoria {
  semilla: number;
  /** Réplicas fijas (si `replicasAutomaticas` es `false`). */
  replicas: number;
  /**
   * Réplicas automáticas: el motor empieza con 20 y agrega réplicas hasta que los indicadores
   * principales alcanzan la precisión exigida, con un máximo de 200 (doc. 04 §11).
   */
  replicasAutomaticas: boolean;
  /** Jornadas del horizonte (1 por defecto). */
  dias: number;
  /**
   * Jornadas de calentamiento al inicio (paso 1.6, avance 4; doc. 04 §8): se simulan con el taller
   * vacío al empezar, pero no entran en los indicadores. Sin el campo: 1 si el horizonte es de 3
   * jornadas o más; si no, ninguna.
   */
  calentamiento?: number;
  /**
   * La última jornada del horizonte es de cierre (paso 1.6, avance 5): se simula con sus llegadas
   * para que los autos de la jornada anterior terminen, pero no se mide. Sin el campo: sí desde 3
   * jornadas.
   */
  jornadaDeCierre?: boolean;
}

/**
 * De dónde salen las medidas del marco (asistente «Configura tu taller», paso 1.2). El terreno es un
 * dato real: si el asistente propone otras medidas porque el layout no cabe, quedan como supuesto y
 * se conservan las pedidas.
 */
export interface OrigenDeMedidas {
  origen: 'ingresado' | 'supuesto';
  /** Por qué es supuesto: del perfil de partida o propuesta del asistente. */
  fuente?: 'perfil' | 'propuesta';
  /** Medidas pedidas (respondidas o del perfil) cuando se usó la propuesta del asistente. */
  pedidas?: { ancho: number; fondo: number; origen: 'ingresado' | 'supuesto' };
}

export interface Espacio {
  id: string;
  nombre: string;
  /** Marco visible de la operación: centro y medidas en metros. */
  marco: { x: number; z: number; ancho: number; fondo: number };
  /** Origen de las medidas del marco; si falta, no se indica (plantillas y proyectos anteriores). */
  medidas?: OrigenDeMedidas;
  layout: Layout;
  modelo: ModeloOperativo;
  configuracionAleatoria: ConfiguracionAleatoria;
  /**
   * Semáforo (balizas) propio del negocio. Si falta, se usa el por defecto para el proceso. No
   * cambia los resultados del motor: solo cómo se ven los autos.
   */
  semaforo?: DefinicionDeSemaforo;
}

export type OrigenProyecto =
  | 'plantilla:taller-base'
  | 'plantilla:taller-demo'
  | 'lienzo'
  | 'asistente'
  | 'importado'
  | 'duplicado'
  | `caso:${string}`;

export interface Proyecto {
  versionEsquema: number;
  id: string;
  espacioDeTrabajoId: string;
  /** Carpeta dentro del espacio de trabajo (paso 1.5); sin carpeta si falta. */
  carpetaId?: string;
  nombre: string;
  descripcion: string;
  origen: OrigenProyecto;
  creadoEn: string;
  actualizadoEn: string;
  espacios: Espacio[];
}

/** Rol operativo de un elemento del catálogo: qué estación produce al compilar el layout. */
export interface VarianteDeEstacion {
  nombre: string;
  /** Se prefiere para los tipos de unidad que la admiten (cada servicio dice cuáles admite). */
  especializada: boolean;
  /** `false` = la estación no se usa en la simulación (por ejemplo, elevador inactivo). */
  activa: boolean;
}

export interface RolOperativo {
  estacion: TipoEstacion;
  /** Capacidad de la estación; `null` = sin límite (ingreso, salida). */
  capacidad: number | null;
  /** Sigue el turno del espacio (no inicia trabajo fuera de él). */
  sigueTurno: boolean;
  /** Variantes por tipo de bahía (solo elevadores). */
  variantes?: Partial<Record<TipoBahia, VarianteDeEstacion>>;
}

/** Categorías del catálogo, en el orden del v6 (paso 1.3). */
export type CategoriaCatalogo =
  'pisos' | 'operacion' | 'puntos-clave' | 'equipos' | 'clientes' | 'exterior' | 'estructura';

/**
 * Boca de acceso de un elemento (paso 1.3): por dónde se conecta con la circulación. Es un dato del
 * catálogo; el compilador la usa para las estaciones y la 1.4 la dibuja en el plano.
 *
 * - `detras`: una boca detrás del elemento (−z local), como un elevador;
 * - `paso`: entra por detrás y sale por delante, como un cajón de recepción o un portón;
 * - `delante`: una boca delante (+z local), como la puerta de la oficina;
 * - `marcador`: el elemento es un punto que se pega a la vía más cercana (ingreso, salida).
 */
export interface BocaDeAcceso {
  forma: 'detras' | 'paso' | 'delante' | 'marcador';
  /** Qué circulación la usa: autos (vías vehiculares) o personas (sendas peatonales). */
  circulacion: 'vehicular' | 'peatonal';
}

/**
 * Familia de un elemento en el plano: la 1.4 («Plano 2D») y los planos de revisión dan a cada familia
 * su color. Así el color no queda escrito elemento por elemento.
 */
export type FamiliaDePlanta =
  | 'bahia'
  | 'cajon'
  | 'lavado'
  | 'edificio'
  | 'acceso'
  | 'equipo'
  | 'mobiliario'
  | 'vegetacion'
  | 'urbano'
  | 'obra'
  | 'pintura';

/** Símbolo del elemento en planta (paso 1.3): lo dibuja el «Plano 2D» de la 1.4. */
export interface SimboloDePlanta {
  /** Contorno: la huella rectangular, un círculo inscrito o una marca pintada (sin borde). */
  forma: 'rectangulo' | 'circulo' | 'marca';
  familia: FamiliaDePlanta;
  /** Rótulo corto (hasta 4 letras) para leer el plano sin abrir el elemento. */
  rotulo?: string;
}

export interface ItemDeCatalogo {
  id: string;
  nombre: string;
  categoria: CategoriaCatalogo;
  descripcion: string;
  dimensionesPorDefecto: { ancho: number; fondo: number; alto: number };
  medidasMinimas?: { ancho: number; fondo: number };
  /** Afecta al modelo («Simula»). */
  simula: boolean;
  /** Paso en que un elemento hoy «Solo visual» empieza a simular (por ejemplo, la torre: '1.6'). */
  simulaDesde?: string;
  /** Solo un elemento de este tipo por espacio. */
  unico?: boolean;
  rolOperativo?: RolOperativo;
  /** Boca de acceso; `null` = no se conecta con la circulación (decoración). */
  boca: BocaDeAcceso | null;
  simbolo: SimboloDePlanta;
  /**
   * Ocupa su huella en el piso: dos elementos sólidos no pueden superponerse. Las marcas de piso, el
   * techado, el rótulo y lo que cruza una vía a propósito (portón, barrera, puerta) no lo son.
   */
  solido: boolean;
  /**
   * Puede tener equipos y mobiliario dentro sin aviso de superposición (oficina, almacén, edificio,
   * sala de espera), como en el v6.
   */
  contenedor?: boolean;
  /** Qué más se edita en Propiedades: el alto y un texto (rótulo, letrero). */
  editable?: { alto?: boolean; texto?: string };
  licencia: 'propia';
}

export type Catalogo = Record<string, ItemDeCatalogo>;
